import Hls from 'hls.js'
import { ComponentProps, useEffect, useRef } from 'react'

import { AuthService } from '@/api/models/auth'
import { BEARER } from '@/api/models/auth/lib/auth.service'

export type HlsPlayerProps = ComponentProps<'video'> & {
  withAuth?: boolean
  failOnUnauthorized?: boolean
  onReady?: () => void
  onServerError?: () => void
  onForbidden?: () => void
}

const MEDIA_MTX_HOST = 'mtx.sminex.com'

const isMediaMtxStream = (url: string) =>
  new URL(url).hostname === MEDIA_MTX_HOST

const isServerErrorStatus = (status?: number) =>
  status === 404 || (typeof status === 'number' && status >= 500)

const HlsPlayer = ({
  src = '',
  controls = true,
  withAuth = true,
  failOnUnauthorized = false,
  onReady,
  onServerError,
  onForbidden,
  ...props
}: HlsPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls>()
  const onReadyRef = useRef(onReady)
  const onServerErrorRef = useRef(onServerError)
  const onForbiddenRef = useRef(onForbidden)
  const failOnUnauthorizedRef = useRef(failOnUnauthorized)

  onReadyRef.current = onReady
  onServerErrorRef.current = onServerError
  onForbiddenRef.current = onForbidden
  failOnUnauthorizedRef.current = failOnUnauthorized

  useEffect(() => {
    let hls: Hls | undefined
    let isRefreshing = false
    let hasNotified = false
    const video = videoRef.current
    const useMediaMtxAuth = withAuth && Boolean(src) && isMediaMtxStream(src)

    if (!video || !src) {
      return
    }

    const stopStream = () => {
      hlsRef.current?.destroy()
      hlsRef.current = undefined
      hls = undefined
      video.pause()
      video.removeAttribute('src')
      video.load()
    }

    const stopStreamAndNotifyError = () => {
      if (hasNotified) {
        return
      }
      hasNotified = true
      stopStream()
      onServerErrorRef.current?.()
    }

    const stopStreamAndNotifyForbidden = () => {
      if (hasNotified) {
        return
      }
      hasNotified = true
      stopStream()
      onForbiddenRef.current?.()
    }

    const refreshAndRetry = async () => {
      if (!useMediaMtxAuth || isRefreshing || !hls) {
        return
      }

      isRefreshing = true
      try {
        const response = await AuthService.refreshToken()
        if (response?.access_token) {
          hls.loadSource(src)
        } else {
          stopStreamAndNotifyError()
        }
      } finally {
        isRefreshing = false
      }
    }

    const handleHttpStatus = (status?: number) => {
      if (!status) {
        return
      }

      if (status === 403) {
        stopStreamAndNotifyForbidden()
        return
      }

      if (isServerErrorStatus(status)) {
        stopStreamAndNotifyError()
        return
      }

      if (status === 401) {
        if (failOnUnauthorizedRef.current) {
          stopStreamAndNotifyError()
          return
        }

        if (useMediaMtxAuth) {
          void refreshAndRetry()
        }
      }
    }

    if (Hls.isSupported()) {
      hls = new Hls({
        xhrSetup: (xhr: XMLHttpRequest) => {
          if (useMediaMtxAuth) {
            const { accessToken } = AuthService.getTokens()
            if (accessToken) {
              xhr.setRequestHeader('Authorization', BEARER + accessToken)
            }
          }

          xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState === 4) {
              handleHttpStatus(xhr.status)
            }
          })
        }
      })
      hlsRef.current = hls
      hls.loadSource(src)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        onReadyRef.current?.()
        video.play().catch(() => {})
      })
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          handleHttpStatus(data.response?.code)
        }
      })
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      const handleLoadedMetadata = () => {
        onReadyRef.current?.()
        video.play().catch(() => {})
      }

      const handleError = () => {
        stopStreamAndNotifyError()
      }

      video.src = src
      video.addEventListener('loadedmetadata', handleLoadedMetadata)
      video.addEventListener('error', handleError)

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata)
        video.removeEventListener('error', handleError)
        hlsRef.current = undefined
        hls?.destroy()
      }
    }

    return () => {
      hlsRef.current = undefined
      hls?.destroy()
    }
  }, [src, withAuth])

  return <video ref={videoRef} controls={controls} {...props} />
}

export default HlsPlayer
